import { isUnreachableError } from '@coachapp/connectivity';
import {
  createCacheOps,
  createMutationHandlers,
  createOfflineInterceptors,
  createQueueOps,
  createRouteMatcher,
  createSyncEngine,
  type OfflineInterceptorOptions,
} from '@coachapp/offline-core';
import { dexieStorage } from './dexie-storage';
import { TRAINER_ONLINE_ONLY_PATTERNS } from './constants';
import { assertCacheOwner } from './session';
import { invalidateQueryForUrl } from './cache-invalidation';
import { isOffline, reachabilityMonitor } from './reachability';

export const routes = createRouteMatcher(TRAINER_ONLINE_ONLY_PATTERNS);
export const { isOnlineOnly, matchRoute, getCacheKey } = routes;

export const cacheOps = createCacheOps(dexieStorage);

export const queueOps = createQueueOps(dexieStorage, {
  getLastSyncAt: cacheOps.getLastSyncAt,
  matchRoute: routes.matchRoute,
});

export const mutations = createMutationHandlers({
  storage: dexieStorage,
  cacheOps,
  queueOps,
  matchRoute: routes.matchRoute,
  assertCacheOwner,
});

export const { syncNow } = createSyncEngine({
  cacheOps,
  queueOps,
  mutations,
  matchRoute: routes.matchRoute,
  isOffline,
  invalidateQueryForUrl,
});

export const {
  subscribeSyncStatus,
  getSyncStatus,
  hasPendingChangesForUrl,
} = queueOps;

function interceptorOptions(
  scheduleSync?: OfflineInterceptorOptions['scheduleSync'],
): OfflineInterceptorOptions {
  return {
    isOffline,
    routes,
    mutations,
    scheduleSync,
    e2eSkip: () => import.meta.env.VITE_E2E === 'true',
    isUnreachableError,
    onUnreachableError: (error) => {
      reachabilityMonitor.reportFailure(error);
    },
  };
}

const offlineInterceptors = createOfflineInterceptors(interceptorOptions());

export const { isOfflineCacheMiss } = offlineInterceptors;

export function installOfflineInterceptors(
  client: Parameters<typeof offlineInterceptors.install>[0],
  options?: { scheduleSync?: (client: Parameters<typeof offlineInterceptors.install>[0]) => void },
): void {
  if (options?.scheduleSync) {
    createOfflineInterceptors(interceptorOptions(options.scheduleSync)).install(client);
    return;
  }
  offlineInterceptors.install(client);
}
